import { LoaderCircle, Trophy } from 'lucide-react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { BrandLockup } from './components/brand/DatabinexMark';
import { TeamAvatar } from './components/team/Avatar';
import { useArenaData } from './hooks/useArenaData';
import { useHeartbeat } from './hooks/useHeartbeat';
import { AdminPage } from './pages/AdminPage';
import { ArenaPage } from './pages/ArenaPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { LobbyPage } from './pages/LobbyPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResultsPage } from './pages/ResultsPage';
import { useAuth } from './providers/AuthProvider';

export function App() {
  const { user, loading, isAdmin, teamId } = useAuth();
  const data = useArenaData(teamId, Boolean(user && (teamId || isAdmin)));

  useHeartbeat(Boolean(teamId && data.team && data.state && !isAdmin), data.event);

  if (loading) return <LoadingScreen label="در حال چک‌کردن کد ورود…" />;
  if (!user || (!teamId && !isAdmin)) return <LoginPage />;
  if (!data.event) return <LoadingScreen label={data.error ? 'پیکربندی مسابقه در دسترس نیست؛ ابتدا Seed را اجرا کنید.' : 'در حال وصل‌شدن به میدان مسابقه…'} />;

  if (isAdmin) {
    return <Routes><Route path="/leaderboard" element={<LeaderboardPage event={data.event} teams={data.teams} />} /><Route path="*" element={<AdminPage event={data.event} teams={data.teams} states={data.teamStates} codes={data.teamCodes} />} /></Routes>;
  }

  if (!teamId) return <LoginPage />;
  if (!data.teamResolved) return <LoadingScreen label="در حال بازیابی تیم…" />;
  if (!data.team) return <RegisterPage admissionOpen={data.event.admissionOpen} />;
  if (!data.state) return <LoadingScreen label="در حال بازیابی آخرین وضعیت…" />;

  const teamExperience = data.event.status === 'finished'
    ? <ResultsPage team={data.team} state={data.state} winner={data.teams[0]} />
    : data.event.status === 'registration'
      ? <LobbyPage event={data.event} team={data.team} readyCount={data.teams.length} />
      : data.team.status === 'finished' || !data.state.currentQuestionId
        ? <FinishedWaiting team={data.team} />
        : <ArenaPage event={data.event} team={data.team} state={data.state} question={data.question} rank={data.rank} />;

  return (
    <Routes>
      <Route path="/leaderboard" element={<LeaderboardPage event={data.event} teams={data.teams} />} />
      <Route path="/" element={teamExperience} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LoadingScreen({ label }: { label: string }) {
  return <main className="arena-grid grid min-h-screen place-items-center bg-ink text-white"><div className="text-center"><BrandLockup inverse /><LoaderCircle className="mx-auto mt-10 size-7 animate-spin text-brand" /><p className="mt-4 text-sm text-white/45">{label}</p></div></main>;
}

function FinishedWaiting({ team }: { team: NonNullable<ReturnType<typeof useArenaData>['team']> }) {
  return <main className="arena-grid grid min-h-screen place-items-center bg-ink px-6 text-center text-white"><div><TeamAvatar avatarId={team.avatarId} color={team.color} className="mx-auto size-24 rounded-[26px] text-2xl" /><Trophy className="mx-auto mt-8 size-9 text-brand" /><h1 className="mt-4 text-3xl font-black">مسیر تیم شما کامل شد</h1><p className="mt-3 text-slate-400">پاسخ تازه قفل شده؛ تا پایان رسمی مسابقه کنار تابلو بمانید.</p><strong className="mt-7 block font-mono text-4xl">{team.score.toLocaleString('fa-IR')}</strong><span className="text-xs text-white/35">امتیاز فعلی</span></div></main>;
}
